import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rewardApi } from '../../../api/reward.api';
import { AdminDailyPieceUserItem } from '../../../types/Reward.api.type';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { AccessDots } from '../AccessDots';

const PERIOD_OPTIONS = [
  { value: '1D', label: '1일' },
  { value: '3D', label: '3일' },
  { value: '7D', label: '1주일' },
] as const;

const PAGE_SIZE = 20;

export const DailyPiecePanel = () => {
  const [period, setPeriod] = useState('1D');
  const [page, setPage] = useState(0);

  const { data: items, isLoading } = useQuery({
    queryKey: ['adminDailyPieceBreakdown', period],
    queryFn: () =>
      rewardApi.getDailyPieceBreakdown(period).then((res) => res.data.results as AdminDailyPieceUserItem[]),
  });

  const list = items ?? [];
  const totalPages = Math.ceil(list.length / PAGE_SIZE);
  const pageItems = list.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handlePeriod = (value: string) => {
    setPeriod(value);
    setPage(0);
  };

  const fmt = (n: number) => n.toLocaleString('ko-KR');

  return (
    <div className="space-y-4">
      {/* 기간 선택 */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#030213]">일별 조각수령 (유저별)</h3>
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handlePeriod(opt.value)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                period === opt.value ? 'bg-[#030213] text-white' : 'bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        수령 조각이 많은 순. 총 수령 = 투표 + 출석 + 광고시청 + 기타 (광고시청은 모든 광고 보너스 합산)
      </p>
      <p className="flex items-center gap-1.5 text-xs text-gray-500">
        <span>유저명 옆 동그라미 = 최근 7일 접속 (좌 6일 전 → 우 오늘):</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" /> 접속
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-400" /> 미접속
        </span>
      </p>

      {/* 데스크톱: 테이블 */}
      <Card className="overflow-hidden hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">순위</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">유저</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">총 수령</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">투표</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">출석</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">광고시청</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">기타</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      로딩 중...
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((item, idx) => (
                    <tr key={item.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{page * PAGE_SIZE + idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>
                            <span className="text-gray-900">{item.nickname}</span>
                            <span className="text-xs text-gray-400 ml-1">#{item.userId}</span>
                          </span>
                          <AccessDots access={item.recentAccess} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{fmt(item.total)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{fmt(item.vote)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{fmt(item.attendance)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{fmt(item.ad)}</td>
                      <td className="px-4 py-3 text-right text-gray-600">{fmt(item.etc)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 모바일: 카드 리스트 */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="py-8 text-center text-gray-500">로딩 중...</div>
        ) : pageItems.length === 0 ? (
          <div className="py-8 text-center text-gray-500">데이터가 없습니다.</div>
        ) : (
          pageItems.map((item, idx) => (
            <Card key={item.userId}>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    <span className="text-gray-400 mr-1">{page * PAGE_SIZE + idx + 1}.</span>
                    {item.nickname}
                    <span className="text-xs text-gray-400 ml-1">#{item.userId}</span>
                  </span>
                  <span className="text-sm font-bold text-gray-900">{fmt(item.total)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">최근 7일</span>
                  <AccessDots access={item.recentAccess} />
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs text-gray-500">
                  <div>
                    투표<p className="text-gray-700 font-medium">{fmt(item.vote)}</p>
                  </div>
                  <div>
                    출석<p className="text-gray-700 font-medium">{fmt(item.attendance)}</p>
                  </div>
                  <div>
                    광고시청<p className="text-gray-700 font-medium">{fmt(item.ad)}</p>
                  </div>
                  <div>
                    기타<p className="text-gray-700 font-medium">{fmt(item.etc)}</p>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            이전
          </Button>
          <span className="text-sm text-gray-600">
            {page + 1} / {totalPages} ({list.length}명)
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            다음
          </Button>
        </div>
      )}
    </div>
  );
};
